import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { saveDownload } from "@/lib/reporting";
import BrandMark from "@/components/BrandMark";
import {
  SealCheck,
  ClockCounterClockwise,
  Prohibit,
  Question,
  SpinnerGap,
  DownloadSimple,
} from "@phosphor-icons/react";

const inputCls =
  "mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent transition-shadow";
const labelCls = "text-xs uppercase tracking-[0.2em] font-semibold text-slate-500";
const buttonCls =
  "w-full flex items-center justify-center gap-2 rounded-md bg-slate-900 text-white py-2.5 text-sm font-medium hover:bg-slate-800 active:scale-[0.99] transition-all duration-200 disabled:opacity-60";

// One entry per state the API can report, so the page cannot invent a fifth.
// `tone` drives the banner; nothing here is derived from the payload's own
// styling, which a public page must never take from data.
const STATES = {
  verified: {
    tone: "bg-emerald-50 border-emerald-200 text-emerald-900",
    icon: SealCheck,
    iconTone: "text-emerald-600",
    heading: "Verified",
    blurb: "This certificate is authentic and is the current version.",
  },
  superseded: {
    tone: "bg-amber-50 border-amber-200 text-amber-900",
    icon: ClockCounterClockwise,
    iconTone: "text-amber-600",
    heading: "Superseded",
    blurb:
      "This certificate was issued by the laboratory but has since been replaced by a newer version.",
  },
  revoked: {
    tone: "bg-rose-50 border-rose-200 text-rose-900",
    icon: Prohibit,
    iconTone: "text-rose-600",
    heading: "Revoked",
    blurb:
      "The issuing laboratory has withdrawn this certificate. It should not be relied on.",
  },
  notfound: {
    tone: "bg-slate-50 border-slate-200 text-slate-700",
    icon: Question,
    iconTone: "text-slate-400",
    heading: "No matching certificate",
    blurb:
      "We could not find a certificate for that code. Check the characters and try again.",
  },
};

const FIELDS = [
  ["Certificate", "certificate_code"],
  ["Sample", "sample_code"],
  ["Issuing laboratory", "laboratory"],
  ["Client", "client_name"],
  ["Issued", "issued_at"],
  ["Released", "released_at"],
];

function formatMoment(value) {
  if (!value) return "—";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
}

/**
 * Public certificate verification (FR-18).
 *
 * Unauthenticated: a client, or their own customer, confirms a certificate is
 * authentic without an account. Reached by scanning the QR on a COA
 * (`/verify/:token`) or by typing the printed code at `/verify`.
 *
 * Manual entry navigates to the token's own URL rather than posting a form.
 * That keeps every lookup a GET — shareable, re-loadable, and clear of the
 * CSRF middleware, which fires on POSTs whenever a stale session cookie is
 * present and would reject a visitor who merely happens to have signed in
 * here before.
 */
export default function Verify() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [entry, setEntry] = useState("");
  const [result, setResult] = useState(null);
  const [state, setState] = useState(token ? "loading" : "idle");
  const [downloading, setDownloading] = useState(false);

  // Every verification result is a stable public URL, which is exactly what a
  // crawler would index. The API sends X-Robots-Tag for the same reason; this
  // covers the rendered page a browser actually shows.
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  const lookup = useCallback(async (value) => {
    setState("loading");
    try {
      const { data } = await api.get(`/verify/${encodeURIComponent(value)}`);
      setResult(data);
      setState(data.state);
    } catch (err) {
      if (err.response?.status === 404) {
        setResult(null);
        setState("notfound");
        return;
      }
      if (err.response?.status === 429) {
        setResult(null);
        setState("throttled");
        return;
      }
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setState("error");
    }
  }, []);

  useEffect(() => {
    if (token) lookup(token);
    else setState("idle");
  }, [token, lookup]);

  const submit = (event) => {
    event.preventDefault();
    const trimmed = entry.trim();
    if (!trimmed) return;
    navigate(`/verify/${encodeURIComponent(trimmed)}`);
  };

  const download = async () => {
    setDownloading(true);
    try {
      const { data } = await api.get(
        `/verify/${encodeURIComponent(token)}/report.pdf`,
        { responseType: "blob" }
      );
      saveDownload(
        data,
        `certificate-${result?.certificate?.certificate_code || token}.pdf`,
        "application/pdf"
      );
    } catch (err) {
      toast.error("That document is no longer available for download.");
    } finally {
      setDownloading(false);
    }
  };

  // Until a token resolves there is no issuing laboratory to brand for, so the
  // header names the service instead of repeating a placeholder as though it
  // were a lab.
  const brand = result?.branding || null;
  const descriptor = STATES[state];

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-4 sm:p-8">
      <div className="mx-auto w-full max-w-2xl">
        <div
          className="rounded-xl p-6 text-white mb-5 flex items-center gap-3"
          style={{ backgroundColor: brand?.color || "#0F172A" }}
        >
          <div className="h-9 w-9 rounded-md bg-white/15 flex items-center justify-center shrink-0">
            <BrandMark size={20} className="text-white" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate">
              {brand?.label || "Certificate verification"}
            </p>
            <p className="text-xs text-white/80">
              {brand?.label
                ? "Certificate verification"
                : "Confirm a certificate is authentic"}
            </p>
          </div>
        </div>

        {state === "loading" && (
          <div
            className="rounded-lg border border-slate-200 bg-white p-8 flex items-center justify-center gap-2 text-slate-500"
            data-testid="verify-loading"
          >
            <SpinnerGap size={18} className="animate-spin" />
            Checking this certificate…
          </div>
        )}

        {state === "throttled" && (
          <div
            className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-amber-900"
            data-testid="verify-throttled"
          >
            <p className="font-semibold">Too many attempts</p>
            <p className="text-sm mt-1">
              Too many certificates have been checked from this connection.
              Please wait a few minutes and try again.
            </p>
          </div>
        )}

        {descriptor && (
          <div data-testid={`verify-${state}`}>
            <div className={`rounded-lg border p-6 ${descriptor.tone}`}>
              <div className="flex items-start gap-3">
                <descriptor.icon
                  size={28}
                  weight="fill"
                  className={`${descriptor.iconTone} shrink-0`}
                />
                <div>
                  <h1 className="text-lg font-semibold">{descriptor.heading}</h1>
                  <p className="text-sm mt-1">{descriptor.blurb}</p>
                  {state === "revoked" && result?.revoked_at && (
                    <p className="text-sm mt-2 font-medium">
                      Revoked {formatMoment(result.revoked_at)}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {result?.certificate && (
              <div className="mt-5 rounded-lg border border-slate-200 bg-white p-6">
                <h2 className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-4">
                  Certificate details
                </h2>
                <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                  {FIELDS.filter(([, key]) => result.certificate[key]).map(
                    ([label, key]) => (
                      <div key={key}>
                        <dt className="text-xs text-slate-500">{label}</dt>
                        <dd className="text-sm text-slate-900 break-words">
                          {key === "issued_at" || key === "released_at"
                            ? formatMoment(result.certificate[key])
                            : result.certificate[key]}
                        </dd>
                      </div>
                    )
                  )}
                </dl>
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <dt className="text-xs text-slate-500">Document hash (SHA-256)</dt>
                  <dd className="text-xs font-mono text-slate-700 break-all mt-1">
                    {result.certificate.content_hash}
                  </dd>
                </div>
              </div>
            )}

            {state === "superseded" && result?.current_token && (
              <button
                type="button"
                onClick={() => navigate(`/verify/${result.current_token}`)}
                className="mt-4 w-full rounded-md border border-slate-300 bg-white py-2.5 text-sm font-medium text-slate-900 hover:bg-slate-50 transition-colors"
                data-testid="verify-goto-current"
              >
                View the current version
              </button>
            )}

            {(state === "verified" || state === "superseded") && (
              <button
                type="button"
                onClick={download}
                disabled={downloading}
                className={`mt-3 ${buttonCls}`}
                data-testid="verify-download"
              >
                {downloading ? (
                  <SpinnerGap size={16} className="animate-spin" />
                ) : (
                  <DownloadSimple size={16} />
                )}
                Download this certificate
              </button>
            )}
          </div>
        )}

        {(state === "idle" || state === "notfound" || state === "error") && (
          <form
            onSubmit={submit}
            className="mt-5 rounded-lg border border-slate-200 bg-white p-6"
            data-testid="verify-form"
          >
            <label className={labelCls} htmlFor="verify-token">
              Certificate code
            </label>
            <input
              id="verify-token"
              className={inputCls}
              value={entry}
              onChange={(e) => setEntry(e.target.value)}
              placeholder="Enter the code printed beneath the QR code"
              autoComplete="off"
              spellCheck="false"
              data-testid="verify-input"
            />
            <button type="submit" className={`mt-4 ${buttonCls}`} data-testid="verify-submit">
              Verify certificate
            </button>
          </form>
        )}

        <p className="mt-6 text-center text-xs text-slate-400">
          {brand
            ? "Certificates are issued by the laboratory named above. This page confirms a document's authenticity and shows no other laboratory data."
            : "Enter the code printed beneath the QR code on a certificate of analysis to confirm it is authentic."}
        </p>
      </div>
    </div>
  );
}
